import React, { useEffect, useRef } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import { Route, Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import {
  VENUE_SELECTION_URL,
  THANK_YOU_URL,
  CONFIRMATION_ERROR_URL,
  QUOTE_URL,
} from "Constants/constants";
import GuardedRoute from "@gbli-events/common/src/Components/GuardedRoute";
import { canActivateFormStep } from "Guards/FormStepGuard";
import CustomSwitch from "@gbli-events/common/src/Components/CustomSwitch";
import QuoteLoader from "../QuoteLoader/QuoteLoader";
import ThankYouContainer from "../../Containers/ThankYouContainer";
import {
  getFormChildPath,
  getFormChildPathDefinition,
} from "Helpers/URLParser";
import { getFilteredFormStepsConfig, getIsEmbed } from "Selectors/Shared";
import { RxCoverageApplicationProvider } from "Features/CoverageApplication/Components/RxCoverageApplicationProvider";
import { useReactable } from "@reactables/react";
import PaymentService from "Helpers/PaymentService";
import API from "Helpers/API";
import { RxCoverageApplication } from "Features/CoverageApplication/Components/RxCoverageApplication";
import { usePortalOne } from "Hooks/usePortalOne";
import UserEntryDataService from "Helpers/UserEntryDataService";
import PortalOneModalService from "Helpers/PortalOneModalService";
import ConfirmationError from "Features/CoverageApplication/Components/ConfirmationError";
import { Spinner, Modal } from "react-bootstrap";
import ThemeColor from "@gbli-events/common/src/Components/Themed/ThemeColor";

const libraries = ["places"];

const Form = ({ resolveQueryParams, checkBoilerplateReferral }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
    libraries,
    language: "en",
  });
  useEffect(() => {
    const { search } = window.location;

    if (isLoaded) {
      checkBoilerplateReferral(search);
      resolveQueryParams(search);
    }
  }, [resolveQueryParams, checkBoilerplateReferral, isLoaded]);

  const formStepsConfig = useSelector(getFilteredFormStepsConfig);
  const isEmbed = useSelector(getIsEmbed);
  const GuardRedirectComponent = () => (
    <Redirect to={getFormChildPath(VENUE_SELECTION_URL, isEmbed)} />
  );

  const FormSteps = Object.entries(formStepsConfig).map(
    ([key, { component: Component, children }]) => {
      if (key === VENUE_SELECTION_URL) {
        return (
          <Route
            key={key}
            exact
            path={getFormChildPathDefinition(key, isEmbed)}
            render={(props) => (
              <>
                <Component {...props} />
                {children}
              </>
            )}
          />
        );
      }

      return (
        <GuardedRoute
          guards={[canActivateFormStep(key)]}
          key={key}
          path={getFormChildPathDefinition(key, isEmbed)}
          component={Component}
          redirectComponent={GuardRedirectComponent}
        >
          {children}
        </GuardedRoute>
      );
    },
  );

  const paymentService = useRef(new PaymentService(new API())).current;
  const userEntryDataService = useRef(
    new UserEntryDataService(new API()),
  ).current;
  const portalOneModalService = useRef(
    new PortalOneModalService("portal-one-container"),
  ).current;

  const rxCoverageApplication = useReactable(RxCoverageApplication, {
    paymentService,
    userEntryDataService,
    portalOneModalService,
  });

  usePortalOne({ rxCoverageApplication, portalOneModalService });

  const [state] = rxCoverageApplication;
  if (!state) return;

  const {
    payment: {
      confirmPaymentRequest: { loading: confirmingPayment },
    },
  } = state;

  return (
    isLoaded && (
      <RxCoverageApplicationProvider value={rxCoverageApplication}>
        <form>
          <CustomSwitch
            notFoundRedirectUrl={getFormChildPathDefinition(
              VENUE_SELECTION_URL,
              isEmbed,
            )}
          >
            {FormSteps}
            <Route
              path={`${getFormChildPathDefinition(QUOTE_URL, isEmbed)}/:id`}
              component={QuoteLoader}
            />
            <Route
              path={getFormChildPathDefinition(THANK_YOU_URL, isEmbed)}
              component={ThankYouContainer}
            />
            <Route
              path={getFormChildPathDefinition(CONFIRMATION_ERROR_URL, isEmbed)}
              component={ConfirmationError}
            />
          </CustomSwitch>
        </form>
        {confirmingPayment && (
          <Modal show={confirmingPayment} centered>
            <Modal.Body>
              <div className="d-flex align-items-center justify-content-center p-4">
                <ThemeColor variant="primary">
                  <Spinner animation="border" role="status" />
                </ThemeColor>
                <span className="ml-3">
                  Creating your policy… Please do not close this window.
                </span>
              </div>
            </Modal.Body>
          </Modal>
        )}
        <div id="portal-one-container" hidden={confirmingPayment}></div>
      </RxCoverageApplicationProvider>
    )
  );
};

Form.propTypes = {
  resolveQueryParams: PropTypes.func.isRequired,
  checkBoilerplateReferral: PropTypes.func.isRequired,
};

export default Form;
